// app/lib/read-tracker.ts — "heeft deze lezer de stap echt gelezen?"
//
// Twee voorwaarden, allebei nodig:
//
//   A. HET EINDE VAN DE TEKST IS IN BEELD. Een sentinel direct ná .kb-prose,
//      via IntersectionObserver. Bewust LIVE en niet "één keer gezien": bij het
//      monteren zijn de afbeeldingen nog niet geladen, dus dan past een lang
//      artikel soms even in het scherm. Zou de vlag blijven plakken, dan gold
//      het einde als bereikt terwijl de lezer nog bij de eerste alinea zit.
//      Zodra de plaatjes binnenkomen zakt de sentinel weer weg en gaat de vlag
//      dus vanzelf weer uit.
//
//   B. JE BENT ER LANG GENOEG ZICHTBAAR GEWEEST. Alleen tijd waarin het
//      tabblad echt in beeld is telt mee. Dit is er puur om "openen en meteen
//      naar beneden vegen" tegen te houden; A is het echte bewijs.
//
// De timing zit in ReadGate: geen DOM, een injecteerbare klok, en geen timers
// binnenin. De adapter eronder plant het enige setTimeout. Zo is de heuristiek
// te testen zonder browser, en die test is het halve punt van dit bestand.

/** Ondergrens: onder acht seconden ben je aan het navigeren, niet aan het lezen. */
export const DWELL_MIN_MS = 8_000;
/** Bovengrens. Zonder plafond vraagt een lang naslagartikel minuten aan
 *  ononderbroken aanwezigheid, haalt niemand dat in één keer, en vinkt de stap
 *  dus nooit af — een storing die de lezer niet kan zien. */
export const DWELL_MAX_MS = 45_000;
const PER_MINUTE_MS = 15_000;

/** Hoeveel zichtbare tijd deze stap vraagt.
 *
 *  readingMinutes rekent de server op 200 wpm (public-data.ts). Een kwart
 *  daarvan is stevig doorbladeren — ruim genoeg, want voorwaarde A bewijst al
 *  dat de lezer onderaan is gekomen. */
export function dwellTarget(readingMinutes: number): number {
  const m = Number(readingMinutes);
  const base = Number.isFinite(m) && m > 0 ? m * PER_MINUTE_MS : DWELL_MIN_MS;
  return Math.min(DWELL_MAX_MS, Math.max(DWELL_MIN_MS, Math.round(base)));
}

/** De timing zonder DOM. */
export class ReadGate {
  private elapsed = 0;
  private since: number | null = null;
  private atEnd = false;
  private done = false;

  constructor(
    private readonly required: number,
    private readonly now: () => number = Date.now,
  ) {}

  get finished() { return this.done; }

  /** Opgetelde zichtbare tijd tot nu toe. */
  visible(): number {
    return this.elapsed + (this.since === null ? 0 : this.now() - this.since);
  }

  setVisible(v: boolean) {
    if (v) {
      if (this.since === null) this.since = this.now();
    } else if (this.since !== null) {
      this.elapsed = this.visible();
      this.since = null;
    }
  }

  setAtEnd(v: boolean) { this.atEnd = v; }

  /** true zodra beide voorwaarden gehaald zijn — eenmalig. */
  check(): boolean {
    if (this.done || !this.atEnd || this.visible() < this.required) return false;
    this.done = true;
    return true;
  }

  /** Over hoeveel ms kan check() slagen als er niets verandert?
   *  null = voorlopig nooit (niet onderaan, of niet in beeld). */
  msUntilRead(): number | null {
    if (this.done || !this.atEnd || this.since === null) return null;
    return Math.max(0, this.required - this.visible());
  }
}

export interface ReadTrackerOptions {
  /** Leeg element direct ná de artikeltekst. */
  sentinel: Element;
  /** Zichtbare tijd die deze stap vraagt — uit dwellTarget(). */
  required: number;
  /** Wordt hooguit één keer aangeroepen. */
  onRead: () => void;
}

/** Koppelt de gate aan de pagina. Geeft een stop() terug voor de opruiming. */
export function createReadTracker({ sentinel, required, onRead }: ReadTrackerOptions) {
  const gate = new ReadGate(required);
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const settle = () => {
    clearTimeout(timer);
    if (stopped) return;
    if (gate.check()) { stop(); onRead(); return; }
    const left = gate.msUntilRead();
    if (left !== null) timer = setTimeout(settle, left);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const last = entries[entries.length - 1];
      if (!last) return;
      gate.setAtEnd(last.isIntersecting);
      settle();
    },
    // -8%: het einde moet écht boven de vouw staan, niet de onderrand
    // schampen. Zelfde techniek als de scroll-spy in ArticleToc.
    { threshold: 0, rootMargin: '0px 0px -8% 0px' },
  );

  const onVisibility = () => {
    gate.setVisible(document.visibilityState === 'visible');
    settle();
  };
  // pagehide en niet beforeunload: die laatste sloopt de bfcache.
  const onHide = () => { gate.setVisible(false); clearTimeout(timer); };

  function stop() {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    observer.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onHide);
  }

  gate.setVisible(document.visibilityState === 'visible');
  observer.observe(sentinel);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onHide);
  settle();

  return { stop };
}
